<template>
  <TopNav />
  <div class="max-w-3xl px-4 pb-10 mx-auto sm:px-6 lg:px-8">
    <div class="space-y-10 sm:py-6">
      <!-- Profile -->
      <div>
        <h3 class="mb-3 text-lg font-bold leading-6 text-gray-900 capitalize dark:text-gray-50 sm:text-xl">Profile</h3>
        <div class="p-4 bg-white rounded-lg shadow-lg dark:bg-gray-900 sm:space-y-5 md:p-6">
          <div>
            <form @submit="updateProfile">
              <div class="space-y-3">
                <!-- Profile information -->
                <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                  <!-- First Name -->
                  <div class="sm:col-span-3">
                    <label for="firstName" class="block text-sm font-medium text-gray-700 dark:text-gray-200"> First name </label>
                    <div class="mt-1">
                      <input
                        type="text"
                        name="firstName"
                        v-model="userData.firstName"
                        autocomplete="given-name"
                        required
                        class="block w-full border-gray-300 rounded-md shadow-sm focus:border-slate-500 focus:ring-slate-500 dark:bg-gray-900 dark:text-white sm:text-sm"
                      />
                    </div>
                  </div>
                  <!-- Last Name -->
                  <div class="sm:col-span-3">
                    <label for="lastName" class="block text-sm font-medium text-gray-700 dark:text-gray-200"> Last name </label>
                    <div class="mt-1">
                      <input
                        required
                        type="text"
                        name="lastName"
                        v-model="userData.lastName"
                        autocomplete="family-name"
                        class="block w-full border-gray-300 rounded-md shadow-sm focus:border-slate-500 focus:ring-slate-500 dark:bg-gray-900 dark:text-white sm:text-sm"
                      />
                    </div>
                  </div>
                  <!-- Email -->
                  <div class="sm:col-span-3">
                    <label for="email" class="block text-sm font-medium text-gray-700 dark:text-gray-200"> Email address </label>
                    <div class="mt-1">
                      <input
                        required
                        name="email"
                        type="email"
                        v-model="userData.email"
                        autocomplete="email"
                        class="block w-full border-gray-300 rounded-md shadow-sm focus:border-slate-500 focus:ring-slate-500 dark:bg-gray-900 dark:text-white sm:text-sm"
                      />
                    </div>
                  </div>
                  <!-- Phone Number -->
                  <div class="sm:col-span-3">
                    <label for="phone-number-input" class="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-200">Phone</label>
                    <input
                      name="phoneNumber"
                      id="phone-number-input"
                      type="tel"
                      v-model="userData.phoneNumber"
                      autocomplete="tel"
                      class="block w-full mt-1 border-gray-300 rounded-md shadow-sm focus:border-slate-500 focus:ring-slate-500 dark:bg-gray-900 dark:text-white sm:text-sm"
                    />
                  </div>
                </div>
              </div>

              <!-- Notifications -->
              <div>
                <div class="pt-6 mt-4 sm:mt-10">
                  <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-white">Notifications</h3>
                  <p class="text-sm text-gray-500">Notifications/reminders about daily reviews. No spam, ever.</p>
                </div>

                <div class="mt-6 space-y-3">
                  <div class="flex items-center justify-between text-sm text-gray-600 dark:text-gray-300 sm:justify-start">
                    <div class="flex w-1/2 space-x-1 text-sm font-medium text-gray-700 dark:text-gray-200">
                      <v-icon name="co-gmail" scale="1" />
                      <span>Email</span>
                    </div>
                    <Switch
                      v-model="userData.channels.email.notifications"
                      :class="[
                        userData.channels.email.notifications ? 'bg-teal-700 ' : 'bg-gray-200 dark:bg-slate-500',
                        'relative inline-flex h-5 w-10 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-1 focus:ring-slate-500 focus:ring-offset-1',
                      ]"
                    >
                      <span class="sr-only">Email notifications toggle</span>
                      <span
                        aria-hidden="true"
                        :class="[
                          userData.channels.email.notifications ? 'translate-x-5' : 'translate-x-0',
                          'pointer-events-none inline-block h-4 w-4 transform rounded-full  bg-white shadow ring-0 transition duration-200 ease-in-out',
                        ]"
                      />
                    </Switch>
                  </div>

                  <div class="flex items-center justify-between text-sm text-gray-600 dark:text-gray-300 sm:justify-start">
                    <div class="flex w-1/2 space-x-1 text-sm font-medium text-gray-700 dark:text-gray-200">
                      <v-icon name="bi-whatsapp" scale="1" />
                      <span>WhatsApp</span>
                    </div>
                    <Switch
                      v-model="userData.channels.whatsapp.notifications"
                      :class="[
                        userData.channels.whatsapp.notifications ? 'bg-teal-700' : 'bg-gray-200 dark:bg-slate-500',
                        'relative inline-flex h-5 w-10 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-1 focus:ring-slate-500 focus:ring-offset-1',
                      ]"
                    >
                      <span class="sr-only">WhatsApp notifications toggle</span>
                      <span
                        aria-hidden="true"
                        :class="[
                          userData.channels.whatsapp.notifications ? 'translate-x-5' : 'translate-x-0',
                          'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                        ]"
                      />
                    </Switch>
                  </div>

                  <div class="flex items-center justify-between text-sm text-gray-600 dark:text-gray-300 sm:justify-start">
                    <div class="flex w-1/2 space-x-1 text-sm font-medium text-gray-700 dark:text-gray-200">
                      <v-icon name="fa-sms" scale="1" />
                      <span>SMS</span>
                    </div>
                    <Switch
                      v-model="userData.channels.sms.notifications"
                      :class="[
                        userData.channels.sms.notifications ? 'bg-teal-700' : 'bg-gray-200 dark:bg-slate-500',
                        'relative inline-flex h-5 w-10 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-1 focus:ring-slate-500 focus:ring-offset-1',
                      ]"
                    >
                      <span class="sr-only">SMS notifications toggle</span>
                      <span
                        aria-hidden="true"
                        :class="[
                          userData.channels.sms.notifications ? 'translate-x-5' : 'translate-x-0',
                          'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                        ]"
                      />
                    </Switch>
                  </div>
                </div>
              </div>

              <div class="flex justify-end mt-8">
                <button type="submit" class="inline-flex justify-center w-full btn-primary sm:w-auto sm:text-sm">Save</button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <!-- Channels -->
      <div>
        <div class="mb-3">
          <h3 class="text-lg font-bold leading-6 text-gray-900 capitalize dark:text-gray-50 sm:text-xl">Content Saving Channel</h3>
          <p class="mt-1 text-sm text-gray-500">Enable WhatsApp or SMS for saving content from your mobile.</p>
        </div>
        <div class="p-4 bg-white rounded-lg shadow-lg dark:bg-gray-900 sm:space-y-5 md:p-6">
          <div class="space-y-3 text-sm sm:text-base">
            <template v-for="channel in messagingChannels" :key="channel">
              <div class="flex items-center justify-between sm:justify-start">
                <div class="flex items-center w-1/2 space-x-1 font-medium text-gray-700 dark:text-gray-200">
                  <v-icon :name="channel.icon" scale="1" />
                  <span>{{ channel.name }}</span>
                </div>
                <div v-if="channel.connected" class="flex space-x-2 item-center">
                  <CheckIcon class="w-5 h-5 text-green-500" />
                  <span class="text-gray-600 dark:text-gray-400">Connected</span>
                </div>
                <div v-else>
                  <a :href="channel.url" class="text-blue-500">{{ channel.cta }}</a>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
      <!-- Subscription -->
      <div v-if="UserSubscriptionOverview">
        <component :is="UserSubscriptionOverview" />
      </div>
      <!-- Account -->
      <div>
        <h3 class="mb-3 text-lg font-bold leading-6 text-gray-900 capitalize dark:text-gray-50 sm:text-xl">Account</h3>
        <div class="p-4 bg-white rounded-lg shadow-lg dark:bg-gray-900 sm:space-y-5 md:p-6">
          <div>
            <div class="space-y-3 text-sm sm:text-base">
              <div class="flex items-center">
                <div class="w-1/2 font-medium text-gray-700 dark:text-gray-200">Contact</div>
                <div class="flex items-center space-x-5">
                  <a class="text-blue-500" href="mailto:hello@wisery.io">Email</a>
                  <a class="text-blue-500" href="https://twitter.com/WiseryHQ">Twitter</a>
                </div>
              </div>

              <div class="pt-5">
                <button type="button" @click="deleteAccountModalOpen = true" class="inline-flex w-full text-sm font-medium text-red-600 hover:text-red-700 focus:outline-none sm:w-auto">
                  <div v-if="!deletionInProgress">Delete Account</div>
                  <div class="flex items-center" v-if="deletionInProgress">
                    <span>Deleting...</span>
                    <loading-spinner class="w-4 h-4" />
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- <div class="pt-8">
          <div>
            <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-white">
              Subscription
            </h3>
          </div>
          <div class="grid items-center grid-cols-3 mt-6 gap-y-6 gap-x-4">
            <div class="col-span-1 text-sm text-gray-600 dark:text-gray-300">Status</div>
            <div class="col-span-2 text-gray-900 dark:text-white">
              <span v-text="subscription.valid ? 'Active' : 'No Subscription'"></span>
            </div>
            <template v-if="subscription.valid">
              <div class="col-span-1 text-sm text-gray-600 dark:text-gray-300">
                Billing/Invoices
              </div>
              <div class="col-span-2">
                <a
                  :href="subscription.portalLink"
                  class="flex items-center text-blue-500 underline"
                >
                  <span v-if="subscription.portalLink"> Stripe Customer Portal</span>
                  <loading-spinner v-if="!subscription.portalLink" class="w-5 h-5" />
                </a>
              </div>
            </template>
          </div>
        </div> -->
  </div>
  <TransitionRoot as="template" :show="deleteAccountModalOpen">
    <Dialog as="div" class="fixed inset-0 z-10 overflow-y-auto" @close="deleteAccountModalOpen = false">
      <div class="flex items-end justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
          <DialogOverlay class="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" />
        </TransitionChild>

        <span class="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">&#8203;</span>
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          enter-to="opacity-100 translate-y-0 sm:scale-100"
          leave="ease-in duration-200"
          leave-from="opacity-100 translate-y-0 sm:scale-100"
          leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        >
          <div
            class="relative inline-block px-4 pt-5 pb-4 overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:w-full sm:max-w-lg sm:p-6 sm:align-middle"
          >
            <div class="sm:flex sm:items-start">
              <div class="flex items-center justify-center flex-shrink-0 w-12 h-12 mx-auto bg-red-100 rounded-full sm:mx-0 sm:h-10 sm:w-10">
                <ExclamationIcon class="w-6 h-6 text-red-600" aria-hidden="true" />
              </div>
              <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <DialogTitle as="h3" class="text-lg font-medium leading-6 text-gray-900"> Delete Account </DialogTitle>
                <div class="mt-2"></div>
                <div class="mt-2">
                  <p class="text-sm text-gray-500">Are you sure you want to delete your account? All data will be permanently removed from our servers. This action cannot be undone.</p>
                </div>
              </div>
            </div>
            <div class="mt-5 sm:mt-4 sm:ml-10 sm:flex sm:pl-4">
              <button
                type="button"
                class="inline-flex justify-center w-full px-4 py-2 text-base font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:w-auto sm:text-sm"
                @click="deleteAccount"
              >
                Delete
              </button>
              <button
                type="button"
                class="inline-flex justify-center w-full px-4 py-2 mt-3 text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                @click="deleteAccountModalOpen = false"
                ref="cancelButtonRef"
              >
                Cancel
              </button>
            </div>
          </div>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import firebaseUser from "../composables/userAuth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getAuth, signOut } from "firebase/auth";
import { db } from "../firebase";
import TopNav from "../components/nav/TopNav.vue";
import LoadingSpinner from "../components/LoadingSpinner.vue";
import intlTelInput from "intl-tel-input";
import "intl-tel-input/build/css/intlTelInput.css";
import { updateDoc, doc, getDoc } from "firebase/firestore";
import { ref, watchEffect } from "vue";
import { useToast } from "vue-toastification";
import { Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot, Switch } from "@headlessui/vue";
import { ExclamationIcon, CheckIcon } from "@heroicons/vue/outline";
import { useRouter } from "vue-router";
import chromeExtension from "../composables/chromeExtension";
import utilFunctions from "../composables/util";
import { defineAsyncComponent } from "vue";

export default {
  components: {
    TopNav,
    Switch,
    Dialog,
    DialogOverlay,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
    ExclamationIcon,
    LoadingSpinner,
    CheckIcon,
  },
  setup() {
    const { user } = firebaseUser();
    const userData = ref({
      channels: {
        email: {
          id: null,
          notifications: true,
        },
        sms: {
          id: null,
          notifications: false,
        },
        whatsapp: {
          id: null,
          notifications: false,
        },
      },
    });
    const toast = useToast();
    const iti = ref(null);
    const deleteAccountModalOpen = ref(false);
    const router = useRouter();
    const deletionInProgress = ref(false);
    const messagingChannels = ref([]);
    const { extensionInstalled } = utilFunctions();
    const extensionIsInstalled = ref(true);
    extensionInstalled(chromeExtension.id, chromeExtension.image, (installed) => {
      extensionIsInstalled.value = installed;
    });
    const UserSubscriptionOverview = defineAsyncComponent(() => import("../components/UserSubscriptionOverview.vue"));

    watchEffect(async () => {
      if (user.value) {
        const docRef = doc(db, "users", user.value.uid);
        const docSnap = await getDoc(docRef);
        userData.value.channels = docSnap.data().channels;

        if (docSnap.exists()) userData.value = { ...docSnap.data(), ...userData.value };

        const input = document.querySelector("#phone-number-input");
        iti.value = intlTelInput(input, {
          initialCountry: "auto",
          preferredCountries: ["us", "gb", "in", "au", "ca"],
          geoIpLookup: function (callback) {
            fetch("https://ipapi.co/json/")
              .then((response) => response.json())
              .then((json) => {
                callback(json.country_code);
              });
          },
          utilsScript: "https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.15/js/utils.min.js",
        });

        if (userData.value.phoneNumber) iti.value.setNumber(userData.value.phoneNumber);

        messagingChannels.value.push(
          {
            name: "WhatsApp",
            icon: "bi-whatsapp",
            url: `https://wa.me/+18317039919?text=/start%20${user.value.uid}`,
            connected: userData.value?.channels?.whatsapp?.id || null,
            cta: "Connect WhatsApp",
          },
          {
            name: "SMS",
            icon: "fa-sms",
            url: `sms:+18317039919&body=/start%20${user.value.uid}`,
            connected: userData.value?.channels?.sms?.id || null,
            cta: "Connect SMS",
          },
          {
            name: "Chrome Extension",
            icon: "si-googlechrome",
            url: chromeExtension.url,
            cta: "Install Extension",
            recommended: true,
            connected: userData.value.channels?.chrome?.id || extensionIsInstalled.value || null,
            mobile: false,
            desktop: true,
          }
        );
      }
    });

    const updateProfile = async (e) => {
      e.preventDefault();

      const data = new FormData(e.target);
      const profileData = Object.fromEntries(data.entries());

      if (profileData.phoneNumber) {
        profileData.phoneNumber = iti.value.getNumber();
      }

      profileData.channels = {
        email: {
          notifications: userData.value.channels.email.notifications,
          id: userData.value.channels.email.id,
        },
        sms: {
          notifications: userData.value.channels.sms.notifications,
          id: userData.value.channels.sms.id,
        },
        whatsapp: {
          notifications: userData.value.channels.whatsapp.notifications,
          id: userData.value.channels.whatsapp.id,
        },
      };

      // At least one notification method is enabled
      if (!profileData.channels.email.notifications && !profileData.channels.sms.notifications && !profileData.channels.whatsapp.notifications) {
        toast.error("Please enable at least one notification channel");
        return;
      }

      // sms or whatsapp notifications can only be enabled if we have a valid phone number
      if ((profileData.channels.sms.notifications || profileData.channels.whatsapp.notifications) && !profileData.phoneNumber) {
        toast.error("Please enter a valid phone number");
        return;
      }

      try {
        await updateDoc(doc(db, "users", user.value.uid), profileData);
        toast.success("Updated successfully");
      } catch (e) {
        toast.error("Error saving your profile information");
      }
    };

    const deleteAccount = async () => {
      deletionInProgress.value = true;
      const functions = getFunctions();
      const deleteUser = httpsCallable(functions, "deleteUser");
      deleteUser()
        .then((result) => {
          const data = result.data;
          toast.success(data.message);

          // log user out
          const auth = getAuth();
          if (!auth.currentUser) {
            router.push("/");
          } else {
            signOut(auth).then(() => {
              router.push("/");
            });
          }
        })
        .catch((error) => {
          deletionInProgress.value = false;
          toast.error(error.message);
        });
    };

    return {
      user,
      userData,
      updateProfile,
      deleteAccountModalOpen,
      deleteAccount,
      deletionInProgress,
      messagingChannels,
      UserSubscriptionOverview,
    };
  },
};
</script>
<style>
.iti {
  width: 100% !important;
}
</style>
